import React, { useEffect, useState } from "react";
import { gql, useLazyQuery } from "@apollo/client";
import { CheckOutlined, LoadingOutlined } from "@ant-design/icons";
import { Card, Note, Skeleton } from "./style";

export const LINK_PREVIEW_QUERY = gql`
  query LinkPreview($url: String!) {
    linkPreview(url: $url) {
      url
      status
      provider
      playbackMode
      title
      description
      imageUrl
      siteName
      faviconUrl
      domain
    }
  }
`;

// Same rule as the Link field's validator in CreateQuote.
const LOOKS_LIKE_URL = /^(https?:\/\/)?([\w-]+\.+[\w-]+)+([/?].*)?$/;
const DEBOUNCE_MS = 600;

const toStatus = (preview) =>
  preview && preview.status === "READY" && preview.title ? { status: "ready", preview } : { status: "failed", preview: null };

/**
 * Preview for the link being typed: { status: "idle" | "loading" | "ready" | "failed", preview }.
 * `initialPreview` is the preview the backend already stored for a saved item: while the link is
 * unchanged it is used as-is, so opening a saved challenge does not refetch every day's link.
 */
export function useLinkPreview(url, initialPreview) {
  const typed = (url || "").trim();
  const stored = initialPreview && (initialPreview.url || "").trim() === typed ? initialPreview : null;
  const [debounced, setDebounced] = useState("");
  const [load, { data, loading, error }] = useLazyQuery(LINK_PREVIEW_QUERY);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(typed), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [typed]);

  const debouncedValid = !!debounced && LOOKS_LIKE_URL.test(debounced);
  const debouncedIsStored = !!stored && debounced === typed;
  useEffect(() => {
    if (debouncedValid && !debouncedIsStored) load({ variables: { url: debounced } });
  }, [debouncedValid, debouncedIsStored, debounced, load]);

  if (!typed || !LOOKS_LIKE_URL.test(typed)) return { status: "idle", preview: null };
  if (stored) return toStatus(stored);
  const preview = data?.linkPreview;
  // Still typing, still fetching, or holding the previous link's result: all count as loading.
  if (typed !== debounced || loading || (!data && !error) || (preview && preview.url !== debounced)) {
    return { status: "loading", preview: null };
  }
  if (error) return { status: "failed", preview: null };
  return toStatus(preview);
}

/** One line under the Link input; the card itself lives in the phone preview. */
export function LinkPreviewStatus({ status, className }) {
  if (status === "loading") {
    return (
      <Note className={className}>
        <LoadingOutlined /> Fetching preview...
      </Note>
    );
  }
  if (status === "ready") {
    return (
      <Note className={["lp-ready", className].filter(Boolean).join(" ")}>
        <CheckOutlined /> Preview ready
      </Note>
    );
  }
  return null;
}

const ArrowIcon = ({ color }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 17L17 7" />
    <path d="M9 7h8v8" />
  </svg>
);

function SiteIcon({ preview, initial, large }) {
  const [failed, setFailed] = useState(false);
  const className = large ? "lp-tile lp-tile-large" : "lp-tile";
  if (preview.faviconUrl && !failed) {
    return <img className={className} src={preview.faviconUrl} alt="" onError={() => setFailed(true)} />;
  }
  return <div className={className}>{initial}</div>;
}

/** The design's link preview card (image card, or compact card when there is no usable image). */
export function LinkPreviewCard({ status, preview, className }) {
  const [failedImage, setFailedImage] = useState(null);

  if (status === "loading") {
    return (
      <Card className={className} data-testid="link-preview-skeleton">
        <Skeleton style={{ height: 118, borderRadius: 0 }} />
        <div className="lp-body">
          <Skeleton style={{ height: 10, width: "35%", marginBottom: 10 }} />
          <Skeleton style={{ height: 13, width: "85%", marginBottom: 8 }} />
          <Skeleton style={{ height: 13, width: "60%" }} />
        </div>
      </Card>
    );
  }
  if (status !== "ready" || !preview) return null;

  const site = preview.siteName || preview.domain || "";
  const initial = (site.trim()[0] || "?").toUpperCase();
  const showImage = !!preview.imageUrl && failedImage !== preview.imageUrl;

  if (showImage) {
    return (
      <Card className={className}>
        <div className="lp-image-wrap">
          <img className="lp-image" src={preview.imageUrl} alt="" onError={() => setFailedImage(preview.imageUrl)} />
          <div className="lp-badge lp-badge-image">
            <ArrowIcon color="#106D65" />
          </div>
        </div>
        <div className="lp-body">
          <div className="lp-site">
            <SiteIcon preview={preview} initial={initial} />
            <div className="lp-domain">{preview.domain}</div>
          </div>
          <div className="lp-title">{preview.title}</div>
          {preview.description && <div className="lp-desc">{preview.description}</div>}
        </div>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <div className="lp-body lp-body-compact">
        <div className="lp-badge lp-badge-compact">
          <ArrowIcon color="#4B5D5A" />
        </div>
        <div className="lp-site lp-site-compact">
          <SiteIcon preview={preview} initial={initial} large />
          <div>
            <div className="lp-site-name">{site}</div>
            {preview.siteName && <div className="lp-domain-small">{preview.domain}</div>}
          </div>
        </div>
        <div className="lp-title lp-title-compact">{preview.title}</div>
        {preview.description && <div className="lp-desc">{preview.description}</div>}
      </div>
    </Card>
  );
}
